import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import {
  presetRowsMatch24x7,
  presetRowsMatchFullNonCurfew,
  summarizePresetAvailabilityRows,
  type CurfewConfig,
} from "@/lib/weekly-availability";

export type LightPresetAvailabilityState =
  | { state: "pending" }
  | { state: "error" }
  | {
      state: "ok";
      rows: { weekday: number; startTime: string; endTime: string }[];
    };

export type LightPresetAvailabilityCellProps = {
  canLoadSchedule: boolean;
  availability: LightPresetAvailabilityState;
  siteCurfew: CurfewConfig;
  areaCurfew: CurfewConfig;
  className?: string;
};

export default function LightPresetAvailabilityCell({
  canLoadSchedule,
  availability,
  siteCurfew,
  areaCurfew,
  className,
}: LightPresetAvailabilityCellProps) {
  const is24x7 = useMemo(() => {
    if (availability.state !== "ok") return false;
    return presetRowsMatch24x7(availability.rows);
  }, [availability]);
  
  const isFullNonCurfew = useMemo(() => {
    if (availability.state !== "ok") return false;
    return presetRowsMatchFullNonCurfew(availability.rows, siteCurfew, areaCurfew);
  }, [availability, siteCurfew, areaCurfew]);

  if (!canLoadSchedule) {
    return (
      <span className={className} title="Weekly availability is visible to site admins">
        —
      </span>
    );
  }

  if (availability.state === "pending") {
    return (
      <span className={`tabular-nums ${className ?? ""}`}>…</span>
    );
  }

  if (availability.state === "error") {
    return (
      <span className={className} title="Could not load availability">
        —
      </span>
    );
  }

  const { rows } = availability;

  if (rows.length === 0) {
    return (
      <Badge
        variant="secondary"
        className="font-normal min-w-[6rem] text-center truncate inline-block align-middle bg-orange-600/30"
        title="No preset-specific weekly windows; enforcement falls back to area/site rules."
      >
        None
      </Badge>
    );
  }

  if (isFullNonCurfew) {
    return (
      <Badge
        variant="secondary"
        className="font-normal min-w-[6rem] text-center truncate inline-block align-middle bg-purple-600/30"
        title={summarizePresetAvailabilityRows(rows)}
      >
        Non-curfew
      </Badge>
    );
  }

  if (is24x7) {
    return (
      <Badge
        variant="secondary"
        className="font-normal min-w-[6rem] text-center truncate inline-block align-middle bg-green-600/30"
        title={summarizePresetAvailabilityRows(rows)}
      >
        24 x 7
      </Badge>
    );
  }

  return (
    <Badge
      variant="secondary"
      className="font-normal min-w-[6rem] text-center truncate inline-block align-middle bg-gray-500/30"
      title={summarizePresetAvailabilityRows(rows)}
    >
      {rows.length} Window{rows.length === 1 ? "" : "s"}
    </Badge>
  );
}
